import styles from "./app-shell.module.scss";
import { AppShell as MantineAppShell, rem, ScrollArea } from "@mantine/core";
import type {
    CustomAppShellFactory,
    CustomAppShellSecondaryNavbarFactory,
    IProps,
    ISecondaryNavbarProps,
} from "./app-shell.types";
import { componentWrapper } from "../../utilities";
import { Box } from "../box";
import { createContext, MutableRefObject, useCallback, useContext, useLayoutEffect, useState } from "react";

interface IAppShellContext {
    secondaryNavbarRef: MutableRefObject<HTMLElement | null>;
    registerSecondaryNavbar: (ref: HTMLElement | null) => void;
}

const AppShellContext = createContext<IAppShellContext>({
    registerSecondaryNavbar: () => void 0,
    secondaryNavbarRef: { current: null },
});

const AppShell = componentWrapper<CustomAppShellFactory, IProps>(
    "AppShell",
    ({ secondaryNavbar, ...otherProps }, ref) => {
        const [secondaryNavbarRef, setSecondaryNavbarRef] = useState<MutableRefObject<HTMLElement | null>>({
            current: null,
        });

        const registerSecondaryNavbar = useCallback((ref: HTMLElement | null) => {
            setSecondaryNavbarRef({ current: ref });
        }, []);

        return (
            <AppShellContext.Provider value={{ secondaryNavbarRef, registerSecondaryNavbar }}>
                <MantineAppShell
                    withBorder={false}
                    ref={ref}
                    data-has-secondary-navbar={!!secondaryNavbar && secondaryNavbarRef.current !== null}
                    classNames={{ root: styles["container"], main: styles["main"], header: styles["header"] }}
                    __vars={{ "--app-shell-secondary-navbar-width": rem(secondaryNavbar?.width || 0) }}
                    {...otherProps}
                />
            </AppShellContext.Provider>
        );
    }
);

const AppShellSecondaryNavbar = componentWrapper<CustomAppShellSecondaryNavbarFactory, ISecondaryNavbarProps>(
    "AppShellSecondaryNavbar",
    ({ children, ...otherProps }, ref) => {
        const { secondaryNavbarRef, registerSecondaryNavbar } = useContext(AppShellContext);

        useLayoutEffect(() => {
            return () => {
                registerSecondaryNavbar(null);
            };
        }, [registerSecondaryNavbar]);

        return (
            <Box
                component="nav"
                ref={(ref) => {
                    if (ref !== null && secondaryNavbarRef.current === null) {
                        registerSecondaryNavbar(ref);
                    }
                }}
                className={styles["secondaryNavbar"]}
                {...otherProps}
            >
                <ScrollArea type="hover" offsetScrollbars="y">
                    {children}
                </ScrollArea>
            </Box>
        );
    }
);

AppShell.Aside = MantineAppShell.Aside;
AppShell.Footer = MantineAppShell.Footer;
AppShell.Header = MantineAppShell.Header;
AppShell.Main = MantineAppShell.Main;
AppShell.Navbar = MantineAppShell.Navbar;
AppShell.SecondaryNavbar = AppShellSecondaryNavbar;
AppShell.Section = MantineAppShell.Section;

export { AppShell, AppShellSecondaryNavbar };
